import React, { useMemo, useRef, useState } from 'react'
import { Box, CircularProgress, Typography } from '@mui/material'
import Tree from '../tree/Tree'
import { convertNodesToTreeItems, initiallyOpenFolderIds, type TreeItemType } from '../tree/utils'
import type { TreeViewBaseItem } from '@mui/x-tree-view/models'
import type { GenNode } from '../../types/carbon/models/GenNode'
import type { CheckboxState } from '../../utils/tree'

// VariableRail shows the job's variable tree down the left of the builder:
// the same tree, drawn by the same component, as the Variables page's
// VARIABLE TREE. Open a folder to see its variables, open a variable to see
// its categories (loaded on open from varCodes, which reads no case data).
// On every tab the rows carry the spec editor's checkboxes: tick a variable
// (any answer) or some of its answers, then a green + on the right puts them
// where they go (a line, a cross slot). The tree is the only
// way to pick variables and answers. It shows no respondent counts: counting
// reads every case in the job, far too slow to do on each click.
export interface VariableRailProps {
  // The job's variable tree, as useVariableTree gives it (a variable's loaded
  // categories are its children).
  nodes: GenNode[]
  onOpenVariable: (name: string) => void
  // When set, variables and their answers carry checkboxes,
  // driven by useVariableTree's checked state as in the spec editor. A
  // variable's name still opens and closes it.
  checkboxes?: {
    getCheckboxState: (node: GenNode) => CheckboxState
    onNodeCheckedToggle: (itemId: string, node: GenNode, isChecked: boolean) => void
  }
  isLoading?: boolean
}

type Item = TreeViewBaseItem<TreeItemType>

// convertNodesToTreeItems always sets an id; the type leaves it optional.
const idOf = (item: Item): string => item.id ?? ''
const isFolder = (item: Item) => item.originalNode.type === 'Folder' || item.originalNode.type === 'Section'

// openOnArrival is what the rail opens when the tree first arrives: the same
// folders the Variables page starts with open (initiallyOpenFolderIds, the
// ones the job's variable tree file marks open; on Prius, "Source"). A small
// imported job marks nothing open, so on top of that a tree with no folders
// inside folders and only a few folders ("Source", perhaps "My Constructs")
// opens them all, and its variables show without a click.
const openOnArrival = (nodes: GenNode[], items: Item[]): string[] => {
  const open = initiallyOpenFolderIds(nodes)
  const folders: Item[] = []
  let nested = false
  const walk = (list: Item[], inFolder: boolean) => {
    for (const item of list) {
      if (!isFolder(item)) continue
      folders.push(item)
      if (inFolder) nested = true
      walk(item.children || [], true)
    }
  }
  walk(items, false)
  if (!nested && folders.length <= 3) return Array.from(new Set([...open, ...folders.map(idOf)]))
  return open
}

export const VariableRail: React.FC<VariableRailProps> = ({
  nodes, onOpenVariable, checkboxes, isLoading,
}) => {
  // Items as the Tree component will draw them (same item ids).
  const items = useMemo(() => convertNodesToTreeItems(nodes), [nodes])
  const [expanded, setExpanded] = useState<string[]>([])
  const addExpanded = (ids: string[]) => setExpanded(prev => Array.from(new Set([...prev, ...ids])))

  // Render-phase guards (not effects): open the starting folders once the
  // tree arrives.
  const arrived = useRef(false)
  if (!arrived.current && items.length > 0) {
    arrived.current = true
    addExpanded(openOnArrival(nodes, items))
  }

  const onNodeExpandToggle = (itemId: string, node: GenNode, isExpanded: boolean) => {
    setExpanded(prev => (isExpanded ? [...prev, itemId] : prev.filter(id => id !== itemId)))
    if (isExpanded && node.type === 'Variable' && node.value1) onOpenVariable(node.value1)
  }

  // A "loading" placeholder row is not something to tick.
  const getCheckboxState = checkboxes
    ? (node: GenNode): CheckboxState => (node.type === 'Loading' ? { enabled: false, checked: false } : checkboxes.getCheckboxState(node))
    : undefined

  // Ticking a variable opens it, so its answers show ticked (and can be
  // unticked to narrow it), as in the spec editor.
  const onNodeCheckedToggle = checkboxes
    ? (itemId: string, node: GenNode, isChecked: boolean) => {
      if (isChecked && node.type === 'Variable') addExpanded([itemId])
      checkboxes.onNodeCheckedToggle(itemId, node, isChecked)
    }
    : undefined

  return (
    <Box
      component="aside"
      aria-label="Variables in this job"
      sx={{
        bgcolor: 'action.hover',
        borderRight: { md: theme => `1px solid ${theme.palette.divider}` },
        borderBottom: { xs: theme => `1px solid ${theme.palette.divider}`, md: 'none' },
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        minHeight: 0,
      }}
    >
      {isLoading && <CircularProgress size={18} sx={{ m: 1 }} />}
      {!isLoading && nodes.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>This job has no variables yet.</Typography>
      )}
      {!isLoading && nodes.length > 0 && (
        <Box sx={{ flex: 1, minHeight: { xs: 240, md: 0 }, mx: -1 }}>
          <Tree
            nodes={nodes}
            expanded={expanded}
            selected=""
            searchEnabled
            searchKeepsFolders
            searchDebounce={200}
            searchPlaceholder="Search variables..."
            showTooltips
            onNodeExpandToggle={onNodeExpandToggle}
            getCheckboxState={getCheckboxState}
            onNodeCheckedToggle={onNodeCheckedToggle}
          />
        </Box>
      )}
    </Box>
  )
}

export default VariableRail
